// core
import React, { useEffect } from "react";
import { ReactElement, useContext } from "react";
import { FieldArray, useFormikContext } from "formik";

// hooks
import useSettingsBuilder from "../../hooks/useSettingsBuilder";
import { QuoteGroupContext } from "../../providers/RootContextProvider";
import { canAddAdult, canAddChildren, canDeleteClient, getClientNumberDisplay, numberOfChildren } from "../../../common/shared/clientHelpers";

// components
import ProductSettingsClientWrapper from "../Products/ProductSettingsClientWrapper/ProductSettingsClientWrapper";
import Tooltip from "../../../common/components/Tooltips/Tooltip";
import StreamlineMortgageSettings from "./StreamlineMortgageSettings";
import StreamlineIncomeSettings from "./StreamlineIncomeSettings";
import StreamlineTraumaSettings from "./StreamlineTraumaSettings";
import StreamlineLifeSettings from "./StreamlineLifeSettings";
import StreamlineHealthSettings from "./StreamlineHealthSettings";
import StreamlineChildClientSettings from "./StreamlineChildClientSettings";
import GlobalProductFormMortgage from "../SettingForms/GlobalProductForms/GlobalProductFormMortgage";

// interfaces
import StreamlineBaseClientSettings, {
    IStreamlineSettingsClientProps
} from "./StreamlineBaseClientSettings";
import ProductType, {
    ClientProductTypes
} from "../../../common/interfaces/enums/ProductType";
import { IQuoteSettings } from "../../interfaces/form/IQuoteSettings";
import { IQuoteClient } from "../../interfaces/form/IQuoteClient";

// styles
import cx from "classnames";
import styles from "../Clients/ClientDetailsSection/ClientDetailsSection.module.scss";

const ClientComponent: {
    [key in ProductType]: React.FC<IStreamlineSettingsClientProps>;
} = {
    [ProductType.Life]: StreamlineLifeSettings,
    [ProductType.Trauma]: StreamlineTraumaSettings,
    [ProductType.Health]: StreamlineHealthSettings,
    [ProductType.Income]: StreamlineIncomeSettings,
    [ProductType.Mortgage]: StreamlineMortgageSettings,
    [ProductType.WaiverOfPremium]: null
};

interface IClientSettingsStreamlineSectionProps {
    currentProduct: ClientProductTypes;
    isLoading: boolean;
}

const ClientSettingsStreamlineSection: React.FC<IClientSettingsStreamlineSectionProps> = (
    props
): ReactElement => {
    const { currentProduct } = props;
    const { values } = useFormikContext<IQuoteSettings>();
    const { actions } = useContext(QuoteGroupContext);

    const { defaultQuoteClient } = useSettingsBuilder();

    const getDefaultAdult = () => {
        return defaultQuoteClient[currentProduct];
    };

    const getDefaultChild = () => {
        return { ...defaultQuoteClient[currentProduct], age: 1, isChild: true };
    };

    const RenderComponent = ClientComponent[currentProduct];
    if (!RenderComponent) return null;

    const childIndex = values.clients.findIndex((c) => c.isChild);

    const getChildProps = (
        index: number,
        name: string,
        client: IQuoteClient,
        remove: (index: number) => void
    ): IStreamlineSettingsClientProps => {
        return {
            id: `${name}.${index}`,
            key: `${name}-${index}-${client.personId}`,
            name: `${name}.${index}`,
            index: index,
            client: client,
            title: ` Adult ${getClientNumberDisplay(index, values.clients, client)}`,
            onDelete:
                canDeleteClient(values.clients) &&
                (() => {
                    remove(index);
                })
        };
    };

    useEffect(() => {
        if (!props.isLoading) {
            actions.setClients(values.clients);
        }
    }, [values.clients]);

    return (
        <ProductSettingsClientWrapper className="flex-gap-xl">
            <FieldArray name="clients">
                {({ remove, push, name }) => (
                    <React.Fragment>
                        {currentProduct === ProductType.Mortgage && (
                            <div
                                className={cx(
                                    "panel-card-wide padding-horizontal-3xl padding-vertical-xl layout-fit-content-height"
                                )}>
                                <h3 className={cx("title-build-client gap-xl")}>
                                    Mortgage details
                                </h3>
                                <GlobalProductFormMortgage />
                            </div>
                        )}
                        {values.clients
                            .sort((a, b) =>
                                a.isChild === b.isChild ? 0 : a.isChild ? 1 : -1
                            )
                            .filter((client) => !client.isChild)
                            .map((client, index) => {
                                const childProps = getChildProps(
                                    index,
                                    name,
                                    client,
                                    remove
                                );
                                return (
                                    <StreamlineBaseClientSettings {...childProps}>
                                        <RenderComponent {...childProps} />
                                    </StreamlineBaseClientSettings>
                                );
                            })}
                        {currentProduct === ProductType.Health &&
                            numberOfChildren(values.clients) > 0 && (
                                <div
                                    className={cx(
                                        "panel-card-wide padding-horizontal-3xl padding-vertical-xl"
                                    )}>
                                    <StreamlineChildClientSettings
                                        clients={values.clients}
                                        onDelete={remove}
                                        addChild={() => {
                                            push({
                                                ...getDefaultChild(),
                                                personId: 0,
                                                isSelectedDefault: true
                                            });
                                        }}>
                                        <StreamlineHealthSettings
                                            {...getChildProps(
                                                childIndex,
                                                "clients",
                                                values.clients[childIndex],
                                                remove
                                            )}
                                        />
                                    </StreamlineChildClientSettings>
                                </div>
                            )}
                        {canAddAdult(values.clients) && (
                            <button
                                className={cx(
                                    "panel-card-wide",
                                    "padding-2xl",
                                    "layout-flex-row--centered",
                                    "ga_click",
                                    styles["add-client-button"]
                                )}
                                data-ga_category="build-step"
                                data-ga_action="family"
                                data-ga_label={"add-adult"}
                                onClick={() => {
                                    push({
                                        ...getDefaultAdult(),
                                        isSelectedDefault: true
                                    });
                                }}>
                                <h3
                                    className={cx(
                                        "text-brand",
                                        "text-font-size-l",
                                        "text-zero-margin"
                                    )}>
                                    Add another adult
                                </h3>
                            </button>
                        )}
                        {currentProduct === ProductType.Health &&
                            canAddChildren(values.clients) && (
                                <button
                                    className={cx(
                                        "panel-card",
                                        "padding-2xl",
                                        "layout-flex-row--centered",
                                        "ga_click",

                                        styles["add-client-button"]
                                    )}
                                    data-ga_category="build-step"
                                    data-ga_action="family"
                                    data-ga_label={"add-child"}
                                    onClick={() => {
                                        push({
                                            ...getDefaultChild(),
                                            personId: childIndex,
                                            isSelectedDefault: true
                                        });
                                    }}>
                                    <h3
                                        className={cx(
                                            "text-brand",
                                            "text-font-size-l",
                                            "text-zero-margin"
                                        )}>
                                        Add children
                                    </h3>

                                    <Tooltip
                                        className="space-pre-m"
                                        tooltipContent="Children are under the age of 16."
                                    />
                                </button>
                            )}
                    </React.Fragment>
                )}
            </FieldArray>
        </ProductSettingsClientWrapper>
    );
};

export default ClientSettingsStreamlineSection;
